import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import H1 from 'common/typography/H1/H1';
import { Copy, Check } from 'common/images/FaIcons';
import { toastr } from 'react-redux-toastr';

const CopyButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 2px;
  margin-left: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.primaryColor || '#6A833F'};
  opacity: 0.6;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.8;
  }

  svg {
    width: 12px;
    height: 12px;
  }
`;

const TaskId = styled.span`
  font-family: 'Petasense Open Sans', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: ${props => props.theme.primaryColor || '#6A833F'};
  margin-left: 12px;
  padding: 4px 12px;
  background: white;
  border: 1px solid ${props => props.theme.primaryColor || '#6A833F'};
  border-radius: 20px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;

  > span {
    position: relative;

    &:before {
      content: 'T';
      margin-right: 3px;
      color: ${props => props.theme.primaryColor || '#6A833F'};
    }
  }

  &:hover {
    border-color: ${props => props.theme.primaryColor || '#6A833F'};
    color: ${props => props.theme.primaryColor || '#6A833F'};
    ${CopyButton} {
      opacity: 1;
    }
  }
`;

const TaskIdWithCopy = ({ id }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(id.toString());
      setCopied(true);
      toastr.success(t('taskDetailsModal.copiedTaskId'));
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (error) {
      // Fallback for browsers that do not support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = id.toString();
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        toastr.success(t('taskDetailsModal.copiedTaskId'));
        setTimeout(() => {
          setCopied(false);
        }, 2000);
      } catch (err) {
        toastr.error(t('taskDetailsModal.copyTaskIdFailed'));
      }
      document.body.removeChild(textArea);
    }
  };

  return (
    <TaskId>
      <span>{id}</span>
      <CopyButton
        onClick={handleCopy}
        title={t('taskDetailsModal.copyTaskId')}
        type="button"
        aria-label={copied ? t('taskDetailsModal.copiedTaskId') : t('taskDetailsModal.copyTaskId')}
      >
        {copied ? <Check /> : <Copy />}
      </CopyButton>
    </TaskId>
  );
};

const BreadcrumbText = styled.span`
  font-family: 'Petasense Open Sans', sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: #6B7280;
`;

const StyledH1 = styled(H1)`
  font-size: 12px;
  font-weight: 400;
  color: #6B7280;
  padding: 0.5em 0;
  margin-bottom: 0;
`;

const TaskTitleWithId = ({ isEdit, taskId, machineName }) => {
  const { t } = useTranslation();

  return (
    <StyledH1>
      <BreadcrumbText>
        {`${machineName} > ${t('taskDetailsModal.tasksBreadcrumb')} > `}
        {isEdit ? t('taskDetailsModal.editTaskTitle') : t('taskDetailsModal.createTaskTitle')}
        {isEdit && taskId && (
          <TaskIdWithCopy id={taskId} />
        )}
      </BreadcrumbText>
    </StyledH1>
  );
};

TaskTitleWithId.propTypes = {
  isEdit: PropTypes.bool,
  taskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  machineName: PropTypes.string.isRequired
};

TaskTitleWithId.defaultProps = {
  isEdit: false,
  taskId: null
};

export default TaskTitleWithId;
